<template>
  <ValidationObserver ref="validationObserver" v-slot="{ }">
    <div class="setup-content">
      <ContentBlock :required="true">
        <div slot="instructions">
          <h5>{{ $t('Bundle Details') }}</h5>
          <p>{{ $t("Your bundle's basic information.") }}</p>
        </div>
        <div class="basics">
          <UIInput
            class="content-title"
            :title="$t('Bundle Title')"
            rules="required|alphaSpacesNumericRich"
            maxlength="50"
            required
            v-model="bundle.title"
            @change="validate"
          />

          <UIInput
            :textarea="true"
            rows="15"
            maxlength="1000000"
            class="description"
            :title="$t('Description')"
            :instructions="$t('Users will see this text when browsing your content the marketplace.')"
            required
            rules="required"
            v-model="bundle.description"
            @change="validate"
          />

          <div class="media">
            <div class="image">
              <UIFormItem
                :title="$t('Content Image')"
                :instructions="$t('This image will be seen by location operators when browsing your bundle on the marketplace.')"
                required
              ></UIFormItem>
              <FileUploadWithThumbnail
                id="image"
                type="image"
                :ratio="280 * 100 / 600"
                :media="getMedia('image')"
                :specs="$t('25MB maximum, suggested size 600x280px.')"
                accept="image/*"
                :required="true"
                :maxFileSize="25 * 1000000"
                class="mt2"
                @input="onFileUploadInput"
                @clear="onFileUploadClear"
              />
            </div>
          </div>
          <TinyMCEEditor :title="$t('System Requirements')" v-model="bundle.systemRequirements" :options="{height: 200}" rules="required" @blur="validate" />
        </div>
      </ContentBlock>
    </div>
  </ValidationObserver>
</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import ContentBlock from '@/core/shared/components/ContentBlock';
import UIInput from '@/core/shared/components/ui/UIInput';
import UIFormItem from '@/core/shared/components/ui/UIFormItem';
import FileUploadWithThumbnail from '@/core/shared/components/FileUploadWithThumbnail';
import { ValidationObserver } from 'vee-validate';
import TinyMCEEditor from './content-fields/TinyMCEEditor.vue';

@Component({
  components: {
    ContentBlock,
    UIInput,
    UIFormItem,
    TinyMCEEditor,
    FileUploadWithThumbnail,
    ValidationObserver,
  },
  props: {
    bundle: {
      type: Object,
      required: true,
    },
  },
  methods: {
    async validate () {
      const valid = await this.$refs.validationObserver.validate();
      if (valid !== this.formIsValid) {
        this.formIsValid = valid;
        this.$emit('valid', valid);
      }
    },
    getMedia (type) {
      if (this.bundle[`_${type}Blob`]) {
        return this.bundle[`_${type}Blob`];
      };
      const src = this.bundle[`${type}Url`] || this.bundle[`${type}Mp4Url`];
      return src && {
        title: '',
        src,
      };
    },
    onFileUploadInput ({ id, ...media }) {
      this.bundle[`_${id}Blob`] = media;
    },
    onFileUploadClear ({ id }) {
      if (`_${id}Blob` in this.bundle) {
        this.bundle[`_${id}Blob`] = null;
      } else {
        const key = `${id}Url` in this.bundle ? `${id}Url` : `${id}Mp4Url`;
        this.bundle[key] = null;
      }
    },
  },
})
export default class SetupDetails extends Vue {
  formIsValid = false;
};
</script>

<style lang="postcss" scoped>
@import "core/shared/styles";

.basics {
  & > * {
    margin-bottom: var(--spacingMd);

    &:last-child {
      margin-bottom: 0;
    }
  }

  & .required {
    color: var(--colorSBRedFlat);
  }

  & .creators {
    & .developers {
      margin-bottom: var(--spacingMd);
    }

    @media (--tablet) {
      display: flex;

      & > * {
        flex: 1 1 auto;

        &:first-child {
          margin-right: var(--spacingMd);
        }
      }

      & .developers {
        margin-bottom: 0;
      }
    }
  }

  & .label {
    margin-bottom: var(--spacingXS);
  }

  & .helper {
    padding: var(--spacingXS) 0;
  }

  & .headset-type {
    & .buttons {
      display: grid;
      grid-gap: var(--spacingSm);
      row-gap: var(--spacingSm);
      justify-content: space-between;
      @media (--tablet) {
        grid-template-columns: repeat(4, auto);
      }
    }
  }

  & .experience-type {
    @media (--tablet) {
      width: 50%;
    }
  }

  & .media {
    @media (--tablet) {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      grid-gap: var(--spacingMd);
    }
  }
}
</style>
